import React, { useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import OGEAvatar from '@/components/OGE/OGEAvatar'
import Typing from '@/components/Room/Typing'
import { cn } from '@/lib/utils'
import { convertFiltersToDisplayItems } from '@/utils/filterUtils'
import FilterBadge from '@/components/ChatAnalysis/FilterBadge'
import { useAppSelector } from '@/redux/hooks'
import { GameSummaryMetadata } from '@/redux/types/room'
import NegotiationMessagesSkeleton from '@/components/Room/NegotiationMessagesSkeleton'
import LlmModelBadge from '@/components/OGE/LlmModelBadge'

interface MessagesDisplayProps {
  summaryAvailable: boolean
  summaryReady: boolean
  isLoadingMetadata: boolean
  metadata?: GameSummaryMetadata | null
}

const MessagesDisplay: React.FC<MessagesDisplayProps> = ({
  summaryAvailable,
  summaryReady,
  isLoadingMetadata,
  metadata,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const { messages, isGenerating, error, isConnected } = useAppSelector(
    (state) => state.gameAnalysis
  )
  const user = useAppSelector((state) => state.user.user)

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [messages])

  return (
    <div className='overflow-auto flex-1 p-4'>
      {isLoadingMetadata ? (
        <NegotiationMessagesSkeleton />
      ) : !summaryAvailable ? (
        <div className='flex justify-center items-center w-full h-full'>
          <div className='text-center'>
            <p className='mb-2 text-xl text-gray-600'>Summary not available</p>
            <p className='text-gray-600'>
              This game ended before summaries were supported.
            </p>
          </div>
        </div>
      ) : !summaryReady ? (
        <div className='flex justify-center items-center w-full h-full'>
          <div className='text-center'>
            <p className='mb-2 text-xl text-gray-600'>
              Summary is being generated
            </p>
            <p className='text-gray-600'>
              You will be notified when the summary is ready. The analysis panel
              will be available shortly.
            </p>
          </div>
        </div>
      ) : (
        <>
          {error && (
            <div className='p-4 mb-4 bg-red-50 rounded-lg border border-red-200'>
              <p className='text-sm text-red-600'>{error}</p>
            </div>
          )}

          {!isConnected && (
            <div className='p-4 mb-4 bg-yellow-50 rounded-lg border border-yellow-200'>
              <p className='text-sm text-yellow-600'>
                Connecting to analysis...
              </p>
            </div>
          )}

          {messages.length === 0 ? (
            <div className='flex justify-center items-center w-full h-full'>
              <div className='text-center'>
                <p className='mb-2 text-xl text-gray-600'>No questions yet</p>
                <p className='text-gray-600'>
                  Ask a question about the game to get started
                </p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message, index) => {
                const isCurrentUser = message.user === user?.email
                const filterItems =
                  isCurrentUser && message.filters && metadata
                    ? convertFiltersToDisplayItems(
                        message.filters,
                        metadata,
                        false
                      )
                    : []
                const hasFilters = filterItems.length > 0

                return (
                  <div
                    key={`${message.id}-${index}`}
                    className={cn(
                      'flex mb-4',
                      isCurrentUser ? 'justify-end' : 'justify-start'
                    )}
                  >
                    <div
                      className={cn(
                        'max-w-[80%] p-4 rounded-[20px] bg-[#E6ECF1] break-words wrap-anywhere break-normal',
                        isCurrentUser ? 'text-right' : 'text-left'
                      )}
                    >
                      {!isCurrentUser && (
                        <div className='flex gap-2 items-center mb-1'>
                          <OGEAvatar
                            src='/images/avatar-image.svg'
                            fallback={'GA'}
                            variant='sm'
                            isLoading={false}
                          />
                          <span className='text-base font-medium text-dark-blue'>
                            {message.user}
                          </span>
                        </div>
                      )}

                      {/* Display filters for user messages */}
                      {hasFilters && (
                        <div
                          className={cn(
                            'mb-2',
                            isCurrentUser
                              ? 'flex justify-end'
                              : 'flex justify-start'
                          )}
                        >
                          <div className='flex flex-wrap gap-1.5'>
                            {filterItems.map((item, itemIndex) => (
                              <FilterBadge
                                key={`${item.type}-${itemIndex}`}
                                item={item}
                                index={itemIndex}
                                showRemove={false}
                                maxWidth='max-w-[150px]'
                                size='sm'
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      <div
                        className={cn(
                          'text-sm font-medium text-black break-words',
                          message.streaming && !isCurrentUser
                            ? 'animate-pulse'
                            : ''
                        )}
                      >
                        <div className='chat-markdown max-w-none break-words prose prose-sm wrap-anywhere'>
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={{
                              a: ({ href, children }) => (
                                <a
                                  href={href}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                >
                                  {children}
                                </a>
                              ),
                            }}
                          >
                            {message.message}
                          </ReactMarkdown>
                        </div>
                        {message.streaming && !isCurrentUser && (
                          <span className='ml-1 animate-pulse'>▋</span>
                        )}
                      </div>
                      {!isCurrentUser && (
                        <div className='flex justify-end items-center gap-2 mt-2'>
                          <LlmModelBadge
                            llmModelName={message.llm_model_name}
                            llmProvider={message.llm_provider}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}

              {(() => {
                const lastMsg = messages[messages.length - 1]
                const isUserLast =
                  lastMsg && lastMsg.user === user?.email && isGenerating

                if (isUserLast) {
                  return (
                    <div className='flex justify-start mb-4'>
                      <div className='max-w-[80%] p-4 rounded-[20px] bg-[#E6ECF1]'>
                        <div className='flex gap-2 items-center mb-1'>
                          <OGEAvatar
                            src='/images/avatar-image.svg'
                            fallback={'GA'}
                            variant='sm'
                            isLoading={false}
                          />
                          <span className='text-base font-medium text-dark-blue'>
                            Game Analysis Assistant
                          </span>
                        </div>
                        <Typing />
                      </div>
                    </div>
                  )
                }
                return null
              })()}
            </>
          )}
        </>
      )}
      <div ref={messagesEndRef} />
    </div>
  )
}

export default MessagesDisplay
